import type { JSMol } from '@rdkit/rdkit';
import type { ChemMoleculeV1Document } from '../domain/moleculeProjection.ts';
import { createRdKitIndexMapping, documentToRdKitInput, haptoJoins, molecularFormula } from './chemicalValidation.ts';
import { placeholderDocumentForCoordinateGeneration } from './isomerDepiction.ts';
import { loadRdKit } from './rdkitAdapter.ts';

// #139: a bare Molecule element (contracts/drawing-v11.schema.json's moleculeIllustrationElement)
// has no depiction to draw -- rendered as its molecular formula instead, per the schema's own
// stated intent. No stereochemistry is involved (a bare Molecule has none), so this is strictly
// simpler than generateIsomerDepiction (isomerDepiction.ts): it reuses the same placeholder-
// document -> molblock -> RDKit round trip, but stops at formula computation rather than
// continuing on to get_new_coords().
export async function computeMolecularFormula(molecule: ChemMoleculeV1Document): Promise<string> {
  const placeholder = placeholderDocumentForCoordinateGeneration(molecule);
  const rdkit = await loadRdKit();
  let rdkitMolecule: JSMol | null = null;
  try {
    rdkitMolecule = rdkit.get_mol(documentToRdKitInput(placeholder));
    if (!rdkitMolecule?.is_valid()) {
      throw new Error('RDKit could not sanitize this Molecule for formula computation.');
    }
    // ADR 0026: a complex held by hapto bonds is one formula.
    return molecularFormula(rdkitMolecule, haptoJoins(placeholder, createRdKitIndexMapping(placeholder)));
  } finally {
    rdkitMolecule?.delete();
  }
}
